import * as React from "react";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import {
  GroupChangePreview,
  LockChangePreview,
  SyncChangesetPreview,
  UnitAcgMatchPreview,
} from "../../api/ApiClient";
import { apiClientFactory } from "../../helpers";
import { Button } from "components/ui/button";
import { Separator } from "components/ui/separator";
import { Spinner } from "components/ui/spinner";

export interface SyncPreviewModalProps {
  siteId: string;
  siteName: string;
  /** The flag IDs currently selected in the sync button (1=Locks, 2=Units). Defaults to both. */
  syncFlags?: number[];
  onClose: () => void;
  onRunSync: () => void;
  /**
   * Selected operations the preview can't show (e.g. Fobs). Shown as a note, and "Run Sync Now"
   * stays available even when locks and units have no changes.
   */
  unpreviewedNote?: string;
}

const FLAG_NAMES: Record<number, string> = { 1: "Locks", 2: "Units" };

const ACTION_COLORS: Record<string, string> = {
  Added: "#2e7d32",
  Removed: "#c62828",
  Updated: "#e65100",
};

const ActionBadge = ({ action }: { action: string }) => (
  <span
    style={{
      color: ACTION_COLORS[action] ?? "#555",
      fontWeight: 600,
      minWidth: 64,
      display: "inline-block",
    }}
  >
    {action}
  </span>
);

const COLLAPSE_THRESHOLD = 20;

const ChangeList = ({
  label,
  items,
  renderItem,
}: {
  label: string;
  items: unknown[];
  renderItem: (item: unknown, i: number) => React.ReactNode;
}) => {
  const [expanded, setExpanded] = useState(false);
  if (!items.length) return null;
  const collapsed = !expanded && items.length > COLLAPSE_THRESHOLD;
  const visible = collapsed ? items.slice(0, COLLAPSE_THRESHOLD) : items;
  return (
    <div style={{ marginBottom: 16 }}>
      <div className="mb-2 text-sm font-semibold">
        {label} ({items.length})
      </div>
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        {visible.map((item, i) => renderItem(item, i))}
      </ul>
      {items.length > COLLAPSE_THRESHOLD && (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded(!expanded)}
          style={{
            marginTop: 4,
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "#1976d2",
            fontSize: "0.82em",
            padding: 0,
          }}
        >
          {expanded ? "Show less" : `Show all ${items.length}`}
        </button>
      )}
    </div>
  );
};

export const SyncPreviewModal: React.FC<SyncPreviewModalProps> = ({ siteId, siteName, syncFlags = [1, 2], onClose, onRunSync, unpreviewedNote }) => {
  const flagsValue = syncFlags.reduce((acc, f) => acc | f, 0) || 3;
  const flagNames = syncFlags
    .filter(f => FLAG_NAMES[f])
    .map(f => FLAG_NAMES[f])
    .join(", ");
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<SyncChangesetPreview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiClientFactory()
      .syncPreview(siteId, flagsValue)
      .then((result) => {
        // Normalize null (e.g. 204 response) to an empty changeset so the
        // "No changes" state renders correctly instead of a blank dialog.
        if (!cancelled) setPreview(result ?? { locks: [], groups: [], unitAcgMatches: [] });
      })
      .catch((err) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Failed to load preview.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [siteId, flagsValue]);

  const hasChanges =
    preview && (preview.locks.length > 0 || preview.groups.length > 0 || preview.unitAcgMatches?.length > 0);

  return (
    <Dialog
      open
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
    >
      <DialogContent
        aria-describedby={undefined}
        showCloseButton={false}
        className="max-h-[75vh] overflow-y-auto sm:max-w-2xl"
      >
        <DialogHeader>
          <DialogTitle>
            Sync Preview — {siteName}{flagNames ? ` (${flagNames})` : ""}
          </DialogTitle>
        </DialogHeader>
        {unpreviewedNote && (
          <div className="text-sm text-muted-foreground" role="note">
            {unpreviewedNote}
          </div>
        )}
        {loading && (
          <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
            <Spinner />
          </div>
        )}

        {error && (
          <div className="text-destructive">{error}</div>
        )}

        {!loading && preview && (
          <>
            {preview.warning && (
              <div
                style={{
                  background: "#fff3e0",
                  border: "1px solid #fb8c00",
                  borderRadius: 4,
                  padding: "8px 12px",
                  marginBottom: 16,
                }}
              >
              <div className="text-sm text-muted-foreground">
                  ⚠️ {preview.warning}
                </div>
              </div>
            )}

            {!hasChanges && !preview.warning && (
              <div className="text-sm text-muted-foreground">
                {/* With unpreviewed operations selected, the site isn't necessarily up to date. */}
                {unpreviewedNote
                  ? "No lock or unit changes to preview."
                  : "✓ No changes detected — site is up to date."}
              </div>
            )}

            <ChangeList
              label="Locks"
              items={preview.locks}
              renderItem={(item, i) => {
                const lock = item as LockChangePreview;
                return (
                  <li key={lock.systemLockId ?? i} style={{ marginBottom: 4 }}>
                    <ActionBadge action={lock.action} /> {lock.name}
                    {lock.changes?.length > 0 && (
                      <ul style={{ margin: "2px 0 0", paddingLeft: 20, color: "#555", fontSize: "0.85em" }}>
                        {lock.changes.map((c, j) => <li key={j}>{c}</li>)}
                      </ul>
                    )}
                  </li>
                );
              }}
            />

            {preview.locks.length > 0 && preview.groups.length > 0 && (
              <Separator className="mb-4" />
            )}

            <ChangeList
              label="Groups"
              items={preview.groups}
              renderItem={(item, i) => {
                const group = item as GroupChangePreview;
                return (
                  <li key={group.systemGroupId ?? i} style={{ marginBottom: 6 }}>
                    <ActionBadge action={group.action} /> {group.name}
                    {(group.locksAdded.length > 0 || group.locksRemoved.length > 0) && (
                      <ul style={{ margin: "4px 0 0", paddingLeft: 20, color: "#555", fontSize: "0.85em" }}>
                        {group.locksAdded.map((name, j) => (
                          <li key={`a-${j}`} style={{ color: ACTION_COLORS.Added }}>
                            + {name}
                          </li>
                        ))}
                        {group.locksRemoved.map((name, j) => (
                          <li key={`r-${j}`} style={{ color: ACTION_COLORS.Removed }}>
                            − {name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              }}
            />

            {preview.unitAcgMatches?.length > 0 && (
              <>
                {(preview.locks.length > 0 || preview.groups.length > 0) && (
                  <Separator className="mb-4" />
                )}
                <ChangeList
                  label="Unit ACG matches (unit sync)"
                  items={preview.unitAcgMatches}
                  renderItem={(item, i) => {
                    const match = item as UnitAcgMatchPreview;
                    return (
                      <li key={`${match.lockName}::${match.acgName}`} style={{ marginBottom: 4 }}>
                        <ActionBadge action="Added" /> {match.lockName}{" "}
                        <span style={{ color: "#888" }}>→ {match.acgName}</span>
                      </li>
                    );
                  }}
                />
              </>
            )}
          </>
        )}
        <DialogFooter>
          <Button onClick={onClose} type="button" variant="outline">
            Close
          </Button>
        {(hasChanges || (!!unpreviewedNote && !loading && !error)) && (
          <Button
            onClick={() => {
              onRunSync();
              onClose();
            }}
            type="button"
          >
            Run Sync Now
          </Button>
        )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
